import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { sowConversationService } from '../../services/sowConversationService';
import { sowService } from '../../services/sowService';
import { SOW } from '../../models/sow';
import { ChecklistSection } from '../../components/SOW/ChecklistSection';
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  Upload,
  X,
  AlertCircle,
  Camera
} from 'lucide-react';
import { useAuthToken } from '../../components/AuthTokenProvider';

interface ChecklistItem {
  id: string;
  label: string;
  type: 'checkbox' | 'yes_no' | 'select' | 'text';
  options?: string[];
  placeholder?: string;
}

interface ChecklistSectionData {
  id: string;
  title: string;
  type: 'global' | 'job';
  jobType?: string;
  items: ChecklistItem[];
}

interface ImageRequest {
  id: string;
  title: string;
  helpText: string;
  component?: string;
  jobId?: string;
}

type ImageUploadState = 'pending' | 'uploading' | 'analyzing' | 'complete' | 'error';

interface ImageUpload {
  request: ImageRequest;
  state: ImageUploadState;
  file?: File;
  preview?: string;
  imageId?: string;
  error?: string;
}

export const SOWChecklistPage: React.FC = () => {
  const { sowId } = useParams<{ sowId: string }>();
  const navigate = useNavigate();
  const { tokenSet, isLoading: tokenLoading } = useAuthToken();

  const isPublicMode = window.location.pathname.startsWith('/scope/');

  const [sow, setSOW] = useState<SOW | null>(null);
  const [sections, setSections] = useState<ChecklistSectionData[]>([]);
  const [imageRequests, setImageRequests] = useState<ImageRequest[]>([]);
  const [imageUploads, setImageUploads] = useState<Map<string, ImageUpload>>(new Map());
  const [answers, setAnswers] = useState<Record<string, Record<string, any>>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (sowId && (isPublicMode || (tokenSet && !tokenLoading))) {
      loadData();
    }
  }, [sowId, tokenSet, tokenLoading, isPublicMode]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const loadData = async () => {
    if (!sowId) return;

    try {
      setLoading(true);
      setError(null);

      // Load SOW
      const sowData = await sowService.getSOW(sowId, !isPublicMode);
      setSOW(sowData);

      // Load checklists
      const checklistResponse = await sowConversationService.getQuestionnaire(sowId, !isPublicMode);

      // Convert sections to checklist format
      const checklistSections: ChecklistSectionData[] = checklistResponse.sections.map((section: any) => ({
        id: section.id,
        title: section.title,
        type: section.type || 'job',
        jobType: section.jobType,
        items: section.questions.map((q: any) => ({
          id: q.id,
          label: q.text,
          type: q.type === 'single_choice' ? 'select' : q.type,
          options: q.options,
          placeholder: q.placeholder
        }))
      }));

      setSections(checklistSections);

      // Load image requests
      try {
        const imageResponse = await sowConversationService.getImageRequests(sowId, !isPublicMode);
        setImageRequests(imageResponse.images || []);

        // Initialize image upload states and restore already-uploaded images
        const initialUploads = new Map<string, ImageUpload>();
        console.log('Restoring images - Image requests:', imageResponse.images);
        console.log('Restoring images - SOW images:', sowData.images);

        (imageResponse.images || []).forEach((imgRequest: any) => {
          // Check if this image has already been uploaded (match by questionId)
          const existingImage = sowData.images?.find(
            (img: any) => img.questionnaireContext?.questionId === imgRequest.id
          );

          if (existingImage && existingImage.status === 'analyzed') {
            // Image already uploaded and analyzed - restore it
            console.log(`✅ Restoring image for ${imgRequest.id}:`, {
              imageId: existingImage.imageId || existingImage.id,
              url: existingImage.url,
              status: existingImage.status
            });
            initialUploads.set(imgRequest.id, {
              request: imgRequest,
              state: 'complete',
              imageId: existingImage.imageId || existingImage.id,
              preview: existingImage.url
            });
          } else {
            // Image not yet uploaded
            console.log(`⏸️ No existing image for ${imgRequest.id}, setting to pending`);
            initialUploads.set(imgRequest.id, {
              request: imgRequest,
              state: 'pending'
            });
          }
        });
        setImageUploads(initialUploads);
      } catch (imgErr) {
        console.log('No image requests for this project');
        setImageRequests([]);
      }

      // Initialize answers object and restore saved answers
      const initialAnswers: Record<string, Record<string, any>> = {};
      checklistSections.forEach(section => {
        initialAnswers[section.id] = {};
      });

      // Restore saved answers from SOW if they exist
      if (sowData.questionnaireAnswers && sowData.questionnaireAnswers.length > 0) {
        sowData.questionnaireAnswers.forEach((savedAnswer: any) => {
          const sectionId = savedAnswer.section;
          const questionId = savedAnswer.questionId;
          const answer = savedAnswer.answer;

          if (sectionId && questionId && initialAnswers[sectionId]) {
            initialAnswers[sectionId][questionId] = answer;
          }
        });
      }

      setAnswers(initialAnswers);

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load project data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save answers with debouncing
  const saveAnswers = async (answersToSave: Record<string, Record<string, any>>) => {
    if (!sowId) return;

    try {
      setSaving(true);

      // Convert answers format to API format
      const formattedAnswers: Array<{ questionId: string; answer: string | boolean; section?: string }> = [];
      Object.entries(answersToSave).forEach(([sectionId, sectionAnswers]) => {
        Object.entries(sectionAnswers).forEach(([questionId, answer]) => {
          if (answer !== undefined && answer !== null && answer !== '') {
            formattedAnswers.push({
              questionId,
              answer,
              section: sectionId
            });
          }
        });
      });

      if (formattedAnswers.length > 0) {
        await sowConversationService.saveQuestionnaireProgress(sowId, formattedAnswers, !isPublicMode);
      }
    } catch (err) {
      console.error('Error saving progress:', err);
      // Don't show error to user - it's auto-save, not critical
    } finally {
      setSaving(false);
    }
  };

  const handleAnswerChange = (sectionId: string, itemId: string, value: any) => {
    const updatedAnswers = {
      ...answers,
      [sectionId]: {
        ...answers[sectionId],
        [itemId]: value
      }
    };

    setAnswers(updatedAnswers);

    // Debounce auto-save: save 1 second after user stops typing
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveAnswers(updatedAnswers);
    }, 1000);
  };

  const handleImageSelect = async (imageRequestId: string, file: File) => {
    if (!sowId) return;

    const upload = imageUploads.get(imageRequestId);
    if (!upload) return;

    try {
      // Create preview
      const preview = URL.createObjectURL(file);
      setImageUploads(prev => new Map(prev).set(imageRequestId, {
        ...upload,
        state: 'uploading',
        file,
        preview
      }));

      // Upload image
      const imageId = await sowService.uploadImageForAnalysis(sowId, file, !isPublicMode);

      // Analyze
      setImageUploads(prev => new Map(prev).set(imageRequestId, {
        ...upload,
        state: 'analyzing',
        file,
        preview,
        imageId
      }));

      const projectType = sow?.scopeDefinition?.projectType || 'Kitchen';
      await sowService.analyzeSOWImage(sowId, imageId, projectType, {
        type: 'initial_intake',
        component: upload.request.component || '',
        questionId: imageRequestId
      }, !isPublicMode);

      // Complete
      setImageUploads(prev => new Map(prev).set(imageRequestId, {
        ...upload,
        state: 'complete',
        file,
        preview,
        imageId
      }));

    } catch (error) {
      console.error('Error uploading image:', error);
      setImageUploads(prev => new Map(prev).set(imageRequestId, {
        ...upload,
        state: 'error',
        file,
        error: 'Upload failed. Please try again.'
      }));
    }
  };

  const handleRemoveImage = (imageRequestId: string) => {
    const upload = imageUploads.get(imageRequestId);
    if (!upload) return;

    setImageUploads(prev => new Map(prev).set(imageRequestId, {
      request: upload.request,
      state: 'pending'
    }));
  };

  const handleRetryImage = (imageRequestId: string) => {
    const upload = imageUploads.get(imageRequestId);
    if (!upload || !upload.file) return;

    // Reset to pending and trigger re-upload with same file
    setImageUploads(prev => new Map(prev).set(imageRequestId, {
      request: upload.request,
      state: 'pending'
    }));

    // Re-upload the file
    setTimeout(() => {
      if (upload.file) {
        handleImageSelect(imageRequestId, upload.file);
      }
    }, 100);
  };

  const handleSubmit = async () => {
    if (!sowId) return;

    try {
      setSubmitting(true);
      setError(null);

      // Convert answers to format backend expects
      const formattedAnswers: any[] = [];

      sections.forEach(section => {
        const sectionAnswers = answers[section.id] || {};
        Object.entries(sectionAnswers).forEach(([itemId, value]) => {
          // Skip empty answers
          if (value === undefined || value === null || value === '') return;

          // For checkboxes, only include if checked
          if (typeof value === 'boolean' && !value) return;

          formattedAnswers.push({
            questionId: itemId,
            answer: typeof value === 'boolean' ? 'Yes' : value,
            section: section.id
          });
        });
      });

      // Collect uploaded image IDs
      const uploadedImageIds: string[] = [];
      imageUploads.forEach(upload => {
        if (upload.state === 'complete' && upload.imageId) {
          uploadedImageIds.push(upload.imageId);
        }
      });

      // Submit with answers and image IDs
      await sowConversationService.submitQuestionnaire(sowId, formattedAnswers, !isPublicMode);

      // Navigate to overview/estimate page
      const overviewPath = isPublicMode ? `/scope/${sowId}` : `/sow/${sowId}`;
      navigate(overviewPath);

    } catch (err) {
      console.error('Error submitting checklist:', err);
      setError('Failed to submit checklist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    const overviewPath = isPublicMode ? `/scope/${sowId}` : `/sow/${sowId}`;
    navigate(overviewPath);
  };

  // Helper to get image requests for a specific job
  const getImagesForJob = (jobId: string) => {
    return imageRequests.filter(img => img.component === jobId);
  };

  // Check if any images are still processing
  const hasImagesProcessing = Array.from(imageUploads.values()).some(
    upload => upload.state === 'uploading' || upload.state === 'analyzing'
  );

  // Check if any images failed
  const hasImageErrors = Array.from(imageUploads.values()).some(
    upload => upload.state === 'error'
  );

  // Count image upload states
  const imageUploadStats = {
    total: imageRequests.length,
    complete: Array.from(imageUploads.values()).filter(u => u.state === 'complete').length,
    pending: Array.from(imageUploads.values()).filter(u => u.state === 'pending').length,
    processing: Array.from(imageUploads.values()).filter(u => u.state === 'uploading' || u.state === 'analyzing').length,
    error: Array.from(imageUploads.values()).filter(u => u.state === 'error').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading checklists...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center">
          <div className="text-red-600 mb-4">⚠️</div>
          <p className="text-gray-800 mb-4">{error}</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const globalSection = sections.find(s => s.type === 'global');
  const jobSections = sections.filter(s => s.type === 'job');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold">Project Scope & Photos</h1>
            {saving && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </div>
            )}
          </div>
          <p className="text-gray-600">
            Upload photos and tell us what work needs to be done. We'll use this to create your estimate.
          </p>
        </div>

        {/* Global project checklist */}
        {globalSection && (
          <ChecklistSection
            title={globalSection.title}
            items={globalSection.items}
            answers={answers[globalSection.id] || {}}
            onAnswerChange={(itemId, value) => handleAnswerChange(globalSection.id, itemId, value)}
          />
        )}

        {/* Job-specific sections with photos AND checklists */}
        {jobSections.map(section => {
          const jobImages = getImagesForJob(section.id);

          return (
            <div key={section.id} className="space-y-4">
              {/* Section Title */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg px-6 py-4">
                <h2 className="text-xl font-bold">{section.title}</h2>
                <p className="text-blue-100 text-sm mt-1">
                  {jobImages.length > 0 ? 'Photos & checklist for this area' : 'Checklist for this area'}
                </p>
              </div>

              {/* Photo Upload Section (if images requested for this job) */}
              {jobImages.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <Camera className="w-5 h-5 text-blue-600" />
                      Photos
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {jobImages.map(imgReq => {
                        const upload = imageUploads.get(imgReq.id);
                        if (!upload) return null;
                        return (
                          <ImageUploadCard
                            key={imgReq.id}
                            upload={upload}
                            onImageSelect={handleImageSelect}
                            onRemove={handleRemoveImage}
                            onRetry={handleRetryImage}
                          />
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Checklist */}
              <ChecklistSection
                title="What work needs to be done?"
                items={section.items}
                answers={answers[section.id] || {}}
                onAnswerChange={(itemId, value) => handleAnswerChange(section.id, itemId, value)}
              />
            </div>
          );
        })}

        {/* Image Upload Status Summary (if images exist) */}
        {imageRequests.length > 0 && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-gray-900">Photos</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  {imageUploadStats.complete > 0 && (
                    <span className="text-green-700 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      {imageUploadStats.complete} uploaded
                    </span>
                  )}
                  {imageUploadStats.processing > 0 && (
                    <span className="text-blue-700 flex items-center gap-1">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {imageUploadStats.processing} processing
                    </span>
                  )}
                  {imageUploadStats.error > 0 && (
                    <span className="text-red-700 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {imageUploadStats.error} error
                    </span>
                  )}
                  {imageUploadStats.pending > 0 && (
                    <span className="text-gray-600">
                      {imageUploadStats.pending} pending
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit button */}
        <div className="flex flex-col items-center gap-3">
          <Button
            onClick={handleSubmit}
            disabled={submitting || hasImagesProcessing || hasImageErrors}
            className="px-12"
            size="lg"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating Estimate...
              </>
            ) : hasImagesProcessing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing Photos...
              </>
            ) : hasImageErrors ? (
              <>
                <AlertCircle className="w-5 h-5 mr-2" />
                Fix Image Errors
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Get Estimate
              </>
            )}
          </Button>

          {hasImagesProcessing && (
            <p className="text-sm text-blue-600 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              Please wait for all photos to finish uploading
            </p>
          )}

          {hasImageErrors && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              Please fix or retry failed photo uploads before continuing
            </p>
          )}

          {imageUploadStats.error > 0 && (
            <p className="text-sm text-amber-600 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              Some photos failed to upload. You can still submit or retry them.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Image Upload Card Component
interface ImageUploadCardProps {
  upload: ImageUpload;
  onImageSelect: (id: string, file: File) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}

const ImageUploadCard: React.FC<ImageUploadCardProps> = ({ upload, onImageSelect, onRemove, onRetry }) => {
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImageSelect(upload.request.id, file);
    }
  };

  const getStateIndicator = () => {
    switch (upload.state) {
      case 'pending':
        return <Badge variant="outline" className="text-gray-500">Optional</Badge>;
      case 'uploading':
        return (
          <Badge variant="outline" className="text-blue-600">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Uploading
          </Badge>
        );
      case 'analyzing':
        return (
          <Badge variant="outline" className="text-blue-600">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Analyzing
          </Badge>
        );
      case 'complete':
        return (
          <Badge className="bg-green-600 text-white">
            <CheckCircle className="w-3 h-3 mr-1" />
            Complete
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="outline" className="text-red-600 border-red-300">
            <AlertCircle className="w-3 h-3 mr-1" />
            Error
          </Badge>
        );
    }
  };

  return (
    <div className={`border-2 rounded-lg p-4 ${
      upload.state === 'complete' ? 'border-green-300 bg-green-50/30' :
      upload.state === 'error' ? 'border-red-300 bg-red-50/30' :
      'border-gray-200'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-medium text-sm">{upload.request.title}</h4>
          <p className="text-xs text-gray-600 mt-1">{upload.request.helpText}</p>
        </div>
        {getStateIndicator()}
      </div>

      {upload.state === 'pending' ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors bg-gray-50">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id={`upload-${upload.request.id}`}
          />
          <label htmlFor={`upload-${upload.request.id}`} className="cursor-pointer flex flex-col items-center gap-2">
            <Upload className="h-6 w-6 text-gray-400" />
            <span className="text-sm text-gray-600">Click to upload</span>
          </label>
        </div>
      ) : (
        <div className="relative">
          {upload.preview && (
            <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
              <img
                src={upload.preview}
                alt={upload.request.title}
                className="w-full h-32 object-cover"
                onError={(e) => {
                  // Fallback if image URL fails to load (e.g., expired presigned URL)
                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EImage%3C/text%3E%3C/svg%3E';
                  console.warn('Failed to load image preview - URL may be expired');
                }}
              />
              {upload.state === 'complete' && (
                <button
                  onClick={() => onRemove(upload.request.id)}
                  className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                >
                  <X className="h-4 w-4 text-gray-600" />
                </button>
              )}
            </div>
          )}
          {upload.state === 'error' && (
            <div className="mt-2 space-y-2">
              <div className="text-sm text-red-600 flex items-start gap-1">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{upload.error || 'Upload failed'}</span>
              </div>
              <Button
                onClick={() => onRetry(upload.request.id)}
                variant="outline"
                size="sm"
                className="w-full text-sm"
              >
                Retry Upload
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
